import { Controller } from "@hotwired/stimulus"
import Cropper from "cropperjs"

// Connects to data-controller="profile-picture"
export default class extends Controller {
  static targets = [
    "input",           // File input for gallery (settings form)
    "cameraInput",     // File input for camera capture
    "galleryInput",    // File input for gallery (registration form)
    "preview",         // Preview container
    "placeholder",     // Placeholder icon (registration form)
    "cropModal",       // Crop modal container
    "cropContainer"    // Container for cropper image
  ]

  connect() {
    this.cropper = null
    this.croppedBlob = null
    this.activeInput = null // Track which input triggered the crop
    this.setupWebcamListener()
  }

  disconnect() {
    if (this.cropper) {
      this.cropper.destroy()
    }
    this.disconnectWebcamListener()
  }

  openWebcam() {
    const modal = document.getElementById("webcam-modal")
    if (modal) {
      window.dispatchEvent(new CustomEvent("webcam:open"))
    }
  }

  setupWebcamListener() {
    this.boundHandleWebcamCaptured = (event) => this.handleWebcamCaptured(event)
    window.addEventListener("webcam:captured", this.boundHandleWebcamCaptured)
  }

  disconnectWebcamListener() {
    if (this.boundHandleWebcamCaptured) {
      window.removeEventListener("webcam:captured", this.boundHandleWebcamCaptured)
    }
  }

  handleWebcamCaptured(event) {
    const file = event.detail?.file
    if (!file) return

    const targetInput = this.hasInputTarget ? this.inputTarget : (this.hasGalleryInputTarget ? this.galleryInputTarget : null)
    if (!targetInput) return

    const url = URL.createObjectURL(file)
    this.updatePreview(url)

    const dataTransfer = new DataTransfer()
    dataTransfer.items.add(file)
    targetInput.files = dataTransfer.files
  }

  // Handle file selection from gallery (settings form) - shows crop modal
  selectImage(event) {
    this.activeInput = event.target
    this.handleFileSelect(event)
  }

  // Handle camera capture (Take Photo) - use photo directly, no crop modal (like media_upload)
  handleCameraCapture(event) {
    const file = event.target.files[0]
    if (!file) return

    if (!file.type.match('image.*')) {
      alert('Please select an image file')
      return
    }

    const url = URL.createObjectURL(file)
    this.updatePreview(url)

    const dataTransfer = new DataTransfer()
    dataTransfer.items.add(file)
    this.inputTarget.files = dataTransfer.files

    event.target.value = ''
  }

  // Handle file selection from gallery or camera (registration form)
  handleFileSelect(event) {
    const file = event.target.files[0]
    if (!file) return

    // Check if it's an image
    if (!file.type.match('image.*')) {
      alert('Please select an image file')
      return
    }

    // Store reference to active input if not already set
    if (!this.activeInput) {
      this.activeInput = event.target
    }

    // Read file and show crop modal
    const reader = new FileReader()
    reader.onload = (e) => {
      this.showCropModal(e.target.result)
    }
    reader.readAsDataURL(file)
  }

  showCropModal(imageData) {
    // Show modal
    this.cropModalTarget.classList.remove('hidden', 'pointer-events-none')
    this.cropModalTarget.classList.add('pointer-events-auto')
    
    // Create image element
    const img = document.createElement('img')
    img.src = imageData
    img.style.maxWidth = '100%'
    
    // Clear container and append image
    this.cropContainerTarget.innerHTML = ''
    this.cropContainerTarget.appendChild(img)
    
    // Initialize Cropper after image loads
    img.onload = () => {
      this.cropper = new Cropper(img, {
        aspectRatio: 1,
        viewMode: 1,
        dragMode: 'move',
        autoCropArea: 1,
        restore: false,
        guides: true,
        center: true,
        highlight: false,
        cropBoxMovable: true,
        cropBoxResizable: true,
        toggleDragModeOnDblclick: false,
      })
    }
  }

  cancelCrop() {
    this.closeCropModal()
    // Reset file inputs
    this.resetInputs()
  }

  applyCrop() {
    if (!this.cropper) return

    // Get cropped canvas
    const canvas = this.cropper.getCroppedCanvas({
      width: 400,
      height: 400,
      imageSmoothingEnabled: true,
      imageSmoothingQuality: 'high',
    })

    if (!canvas) {
      console.error('Failed to get cropped canvas')
      return
    }

    // Convert canvas to blob
    canvas.toBlob((blob) => {
      if (!blob) return

      // Create object URL for preview
      const url = URL.createObjectURL(blob)
      
      // Update preview based on which target exists
      this.updatePreview(url)

      // Store blob for upload
      this.croppedBlob = blob

      // Create a new File object from the blob
      const file = new File([blob], 'profile-picture.jpg', { type: 'image/jpeg' })
      
      // Create a new DataTransfer to store the file
      const dataTransfer = new DataTransfer()
      dataTransfer.items.add(file)
      
      // Update the appropriate input
      this.updateFileInput(dataTransfer.files)

      this.closeCropModal()
    }, 'image/jpeg', 0.9)
  }

  updatePreview(url) {
    if (this.hasPreviewTarget) {
      // Check if preview is an img tag or a container
      if (this.previewTarget.tagName === 'IMG') {
        // Registration form style - show the image and hide placeholder
        this.previewTarget.src = url
        this.previewTarget.classList.remove('hidden')
        if (this.hasPlaceholderTarget) {
          this.placeholderTarget.classList.add('hidden')
        }
      } else {
        // Settings form style - replace inner HTML
        this.previewTarget.innerHTML = `
          <img src="${url}" class="w-full h-full object-cover" alt="Profile Picture">
        `
      }
    }
  }

  updateFileInput(files) {
    // Try to find the appropriate input to update
    if (this.activeInput) {
      this.activeInput.files = files
    } else if (this.hasInputTarget) {
      this.inputTarget.files = files
    } else if (this.hasGalleryInputTarget) {
      this.galleryInputTarget.files = files
    }
  }

  resetInputs() {
    if (this.hasInputTarget) this.inputTarget.value = ''
    if (this.hasCameraInputTarget) this.cameraInputTarget.value = ''
    if (this.hasGalleryInputTarget) this.galleryInputTarget.value = ''
    this.activeInput = null
  }

  closeCropModal() {
    if (this.cropper) {
      this.cropper.destroy()
      this.cropper = null
    }
    
    this.cropModalTarget.classList.add('hidden', 'pointer-events-none')
    this.cropModalTarget.classList.remove('pointer-events-auto')
    this.cropContainerTarget.innerHTML = ''
    this.activeInput = null
  }

}

